// Orchestrator: File in, SourceDocument out. Ingestion only — no analysis.

import type {
  IngestProgress,
  IngestResult,
  SourceDocument,
} from '../../types';
import { extractPdf, findRunningLines } from './pdfExtract';
import { parseSections } from './sectionParse';
import { parseTables } from './tableParse';
import { scanCitations } from './citationScan';
import { readMetadata } from './metadata';
import {
  plural,
  rejectionForReadError,
  textDensity,
  validateFile,
  validateTextLayer,
  warningsFor,
} from './validate';

/** Type and size checks, before any bytes are parsed. */
export function checkFile(file: File): IngestResult | null {
  const rejection = validateFile(file);
  return rejection ? { ok: false, rejection } : null;
}

export async function ingestPdf(
  file: File,
  data: ArrayBuffer,
  onProgress?: (p: IngestProgress) => void,
): Promise<IngestResult> {
  const rejected = checkFile(file);
  if (rejected) return rejected;

  let extracted;
  try {
    extracted = await extractPdf(data, onProgress);
  } catch (error) {
    // The raw exception goes to the console; the user gets written copy.
    console.error('[ingest] could not read the PDF', error);
    return { ok: false, rejection: rejectionForReadError(error) };
  }

  const noText = validateTextLayer(extracted.rawText, extracted.pageCount);
  if (noText) return { ok: false, rejection: noText };

  onProgress?.({
    phase: 'parsing',
    pageCount: extracted.pageCount,
    message: 'Finding sections, tables and references',
  });

  const running = findRunningLines(extracted.pages);
  const sections = parseSections(extracted.pages, running);
  const tables = parseTables(extracted.pages, running);
  const citations = scanCitations(extracted.pages);
  const metadata = readMetadata(extracted.rawText);

  // Section detection falls back to one unnumbered "Document" when it finds no
  // numbered headings. That is a warning, not a refusal.
  const sectionsUnstructured =
    sections.length === 1 && sections[0].number === '';

  const warnings = warningsFor({
    pageCount: extracted.pageCount,
    textChars: textDensity(extracted.rawText),
    sectionsUnstructured,
    sectionCount: sections.length,
    tableCount: tables.length,
  });

  const document: SourceDocument = {
    id: `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    fileName: file.name,
    pageCount: extracted.pageCount,
    rawText: extracted.rawText,
    pages: extracted.pages.map((p) => ({ number: p.number, text: p.text })),
    sections,
    tables,
    citations,
    metadata,
    ingestedAt: new Date().toISOString(),
    warnings,
  };

  onProgress?.({
    phase: 'done',
    pageCount: extracted.pageCount,
    message: `Found ${plural(sections.length, 'section')}, ${plural(tables.length, 'table')}, ${plural(citations.length, 'external reference')}`,
  });

  return { ok: true, document };
}

/**
 * The bundled DOE example, so the tool is usable with no document to hand.
 * It is read exactly as a dropped file is — same parse, same checks — so what
 * a first-time visitor sees is what their own plan will produce.
 */
export const BUNDLED_EXAMPLE = {
  fileName: 'doe-example-mv-plan.pdf',
};

export async function ingestBundledExample(
  onProgress?: (p: IngestProgress) => void,
): Promise<IngestResult> {
  const url = `${import.meta.env.BASE_URL}samples/${BUNDLED_EXAMPLE.fileName}`;
  onProgress?.({ phase: 'reading', message: 'Loading the DOE example plan' });

  let data: ArrayBuffer;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    data = await response.arrayBuffer();
  } catch (error) {
    console.error('[ingest] could not load the bundled example', error);
    return {
      ok: false,
      rejection: {
        reason: 'unreadable',
        message:
          'The example plan couldn’t be opened (it did not load with the page). Nothing left your browser.',
        detail:
          'The example ships with the app, so this normally means the page was served from a stale cache or an offline copy. Reloading the page usually fixes it. Failing that, dropping your own plan in works just as well — the example is only there for people who have none to hand.',
      },
    };
  }

  const file = new File([data], BUNDLED_EXAMPLE.fileName, {
    type: 'application/pdf',
  });
  return ingestPdf(file, data, onProgress);
}
